import Head from "next/head";
import {
  PageViewer,
  cleanPage,
  fetchPage,
  fetchPages,
  renderJsonLd,
  renderMeta,
  types,
  useReactBricksContext,
} from "react-bricks/frontend";
import { getPageTranslations, LatestCaseStudy } from "utils/server";
import { ReactNode } from "react";
import config from "../react-bricks/config";
import { Page } from "components/templates/Page";
import { useLocale, YuLifeLocale } from "@hooks";
import { strapiService } from "@services";
import {
  BricksPreloadedPropsContext,
  BricksPreloadedPropsContextType,
} from "hooks/useBrickStaticProps";
import { serverSideAPIRequestService } from "services/serverSideApiRequest";
import {
  mapNextJsLocaleToBricksLocale,
  mapBricksLocaleToNextJsLocale,
  isLocaleInStaticRoute,
} from "@utils";
import fs from "fs";
import { join } from "path";
import { set } from "lodash";
import { constructHref, getLanguageForHrefLang } from "utils/hreflang";
import { usePathname } from "next/navigation";
import { generateDefaultSchema } from "components/atoms/Meta/generateDefaultSchema";
import { NextPageContext } from "next";
import { StaticRoutes } from "routes/StaticRoutes";
import { Meta } from "components/atoms/Meta";
import { useMetadata } from "hooks/useMetadata";
import dynamic from "next/dynamic";
import { KeyResourcesItemProps } from "components/atoms/KeyResourcesItem";

interface PageProps extends BricksPreloadedPropsContextType {
  page: types.Page;
  errorNoKeys: boolean;
  errorPage: boolean;
  errorHeader: boolean;
  errorFooter: boolean;

  locale: YuLifeLocale;
  keyResources: KeyResourcesItemProps[];
  latestCaseStudy: LatestCaseStudy;
  isHomepage: boolean;
}

const EditablePage: React.FC<PageProps> = ({
  page,
  errorNoKeys,
  errorPage,
  strapiProps,
  rewardProps,

  locale,
  keyResources,
  latestCaseStudy,
  isHomepage,
}) => {
  const { pageTypes, bricks } = useReactBricksContext();
  const pageOk = page ? cleanPage(page, pageTypes, bricks) : null;
  const hideHeader = page?.customValues?.hideHeader;
  const hideFooter = page?.customValues?.hideFooter;
  const noIndex = page?.customValues?.noIndex;

  const metadata = useMetadata("index");

  const Index =
    locale === "jp"
      ? dynamic(() => import("../components/pages/_jp/Home"))
      : dynamic(() => import("../components/pages/Home"));

  if (isHomepage) {
    return (
      <>
        <Meta locale={locale} {...metadata} />
        <Index keyResources={keyResources || []} latestCaseStudy={latestCaseStudy || {}} />
      </>
    );
  }

  return (
    <>
      {pageOk && !errorPage && !errorNoKeys && (
        <>
          <Head>
            {renderMeta(pageOk) as ReactNode}
            {/* {renderJsonLd(pageOk) as ReactNode} */}
            {noIndex && <meta name="robots" content="noindex, nofollow" />}
            {renderJsonLdLegacy(pageOk)}
            {renderCanonicalTags(pageOk)}
          </Head>

          <BricksPreloadedPropsContext.Provider value={{ strapiProps, rewardProps }}>
            <Page
              navColor="transparent"
              latestCaseStudy={strapiProps?.getCaseStudies?.latestCaseStudy as LatestCaseStudy}
              hero={false}
              hideFooter={hideFooter}
              hideHeader={hideHeader}
              fullScreenHero
            >
              <PageViewer page={pageOk} main />
            </Page>
          </BricksPreloadedPropsContext.Provider>
        </>
      )}
    </>
  );
};

export const getStaticProps = async (context, ...rest) => {
  const locale = context.locale as YuLifeLocale;

  let errorNoKeys: boolean = false;
  let notFound: boolean = false;

  if (!config.apiKey) {
    notFound = true;
    return { notFound };
  }

  const { slug } = context.params;

  let cleanSlug = "";

  if (!slug) {
    if (
      !isLocaleInStaticRoute(locale, StaticRoutes.index) &&
      !isLocaleInStaticRoute(locale, StaticRoutes.home)
    ) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        ...(await strapiService.getGenericProps(locale)),
        messages: await getPageTranslations(locale, "home"),
        isHomepage: true,
      },
    };
  }

  if (typeof slug === "string") {
    cleanSlug = slug;
  } else {
    cleanSlug = slug.join("/");
  }

  // JP site needs to be set to live first
  if (context.locale === "jp" && process.env.JAPAN_SITE_LIVE !== "true") {
    return { notFound: true };
  }

  let [page, messages, strapiProps] = await Promise.all([
    fetchPage({
      slug: cleanSlug,
      config,
      language: mapNextJsLocaleToBricksLocale(context.locale),
    })
      .then(({ author, ...page }) => page)
      .catch((e) => {
        notFound = true;
        return null;
      }),
    getPageTranslations(context.locale as YuLifeLocale),
    strapiService.getAllGenericProps(context.locale),
  ]);

  const rewardProps = await getRewardBrickProps(page, context.locale as YuLifeLocale);

  // we only support this for UK locale
  const enabledForCountries = page?.customValues?.enabledForCountries || ["uk"];
  if (context.locale === "uk" && !enabledForCountries.includes(context.locale)) {
    notFound = true;
  }

  // Temporarily fixes an issue where no OG image is set but the featured image is set.
  const pageTyped = page as types.Page;
  if (pageTyped?.meta?.featuredImage && !pageTyped?.meta?.openGraph?.image) {
    set(page, "meta.openGraph.image", page.meta.featuredImage);
  }

  return {
    props: {
      rewardProps,
      strapiProps,
      page,
      errorNoKeys,
      messages,
      keyResources: [],
      latestCaseStudy: {},
      locale,
      isHomepage: false,
    },
    notFound,
    revalidate: 60 * 5, // 5 minutes revalidation
  };
};

export const getStaticPaths = async (context) => {
  if (!config.apiKey) {
    return { paths: [], fallback: true };
  }

  const allPages = await fetchPages(config.apiKey, {
    type: "page",
  });

  // ones we want to actually add
  const paths = [];
  const clashes = [];

  const possiblePaths = allPages
    .map((page) =>
      page.translations.map((translation) => ({
        params: {
          slug: translation.slug === "/" ? [] : [...translation.slug.split("/")],
        },
        locale: mapBricksLocaleToNextJsLocale(translation.language),
      })),
    )
    .flat();

  // if there's a slug clash with an existing static page, it will crash the build for some reason.
  // Let's check first and don't return the path if there's a clash.
  for (const path of possiblePaths) {
    const existingPage = join(__dirname, path.params.slug.join("/"));
    const [exists1, exists2] = [
      fs.existsSync(`${existingPage}.js`),
      fs.existsSync(join(existingPage, "index.js")),
    ];

    if (!exists1 && !exists2) {
      paths.push(path);
      console.log(`Added ${path.params.slug.join("/")} (${path.locale}) to return bricks path`);
    } else {
      clashes.push(path);
      console.warn(
        `${path.params.slug.join("/")} already exists as a page... skipping from bricks to avoid exclusing`,
      );
    }
  }

  if (clashes.length > 0) {
    console.warn("Bricks slug clashes found:", JSON.stringify(clashes, null, 2));
    if (process.env.BRICKS_THROW_ON_SLUG_CLASH === "true") {
      throw new Error("Clashes found");
    }
  }

  return { paths: [...paths, { params: { slug: [""] } }], fallback: "blocking" };
};

export default EditablePage;

/**
 * Pre-fetches data required for reward bricks
 * @param page
 * @param locale
 * @returns
 */
const getRewardBrickProps = async (
  page: Omit<types.Page, "author"> | {} = {},
  locale: YuLifeLocale,
) => {
  if (!page || !("content" in page)) {
    return {};
  }

  // Not required, reward bricks not used. TODO: name it properly
  if (!(page?.content || []).find((p) => p.type.includes("rewards") || p.type === "japan-page")) {
    return {};
  }

  const result = await serverSideAPIRequestService.getRewardProperties(locale);
  return result;
};

const renderCanonicalTags = (page: types.Page) => {
  const hrefLangs = page?.translations?.map((t) => t.language) || [];
  const path = usePathname();
  return [<link key="canonical" rel="canonical" href={constructHref("uk", path)} />].concat(
    hrefLangs.map((hrefLang) => (
      <link
        key={hrefLang}
        rel="alternate"
        href={constructHref(mapBricksLocaleToNextJsLocale(hrefLang), path)}
        hrefLang={getLanguageForHrefLang(mapBricksLocaleToNextJsLocale(hrefLang))}
      />
    )),
  );
};

/** Renders the default organisation JSON-LD */
const renderJsonLdLegacy = (page: types.Page) => {
  const path = usePathname();
  const { locale } = useLocale();
  const canonicalUrl = constructHref(locale, path);
  const description = page?.meta?.description;

  return (
    <script
      id="json-ld"
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: generateDefaultSchema({
          canonicalUrl,
          description,
        }),
      }}
    />
  );
};
